import { logger } from '@/services/logger';

// Base URL for the system configuration API
const API_BASE_URL = (process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + 'system-config';

/**
 * Service for interacting with the system configuration API
 */
export const systemConfigService = {
  /**
   * Get all system configuration properties
   * @returns Promise with the configuration data
   */
  async getAllConfig(): Promise<any> {
    try {
      logger.info('Fetching system configuration');
      
      const response = await fetch(API_BASE_URL);
      
      if (!response.ok) {
        const errorData = await response.json();
        logger.error('Failed to fetch system configuration', { error: errorData, status: response.status });
        throw new Error(errorData.message || `Failed to fetch system configuration (${response.status})`);
      }
      
      const data = await response.json();
      logger.info('Successfully fetched system configuration', { count: data.data?.length || 0 });
      return data.data || [];
    } catch (error) {
      logger.error('Error in getAllConfig', { error });
      throw error;
    }
  },
  
  /**
   * Update a system configuration property
   * @param property The property name to update
   * @param value The new value for the property
   * @returns Promise with the updated configuration data
   */
  async updateConfig(property: string, value: any): Promise<any> {
    try {
      logger.info('Updating system configuration', { property });
      
      const response = await fetch(API_BASE_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ property, value })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        logger.error('Failed to update system configuration', {
          error: errorData,
          status: response.status,
          property
        });
        throw new Error(errorData.message || `Failed to update system configuration (${response.status})`);
      }
      
      const data = await response.json();
      logger.info('Successfully updated system configuration', { property });
      return data.data;
    } catch (error) {
      logger.error('Error in updateConfig', { error, property });
      throw error;
    }
  },

  /**
   * Update multiple system configuration properties at once
   * @param configs Array of property-value pairs to update
   * @returns Promise with the updated configuration data
   */
  async updateMultipleConfigs(configs: Array<{property: string, value: any}>): Promise<any> {
    try {
      logger.info('Updating multiple system configurations', { count: configs.length });
      
      // Create an array of promises for each update
      const updatePromises = configs.map(config =>
        fetch(API_BASE_URL, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ property: config.property, value: config.value })
        })
      );
      
      // Wait for all updates to complete
      const responses = await Promise.all(updatePromises);
      
      // Check if any response failed
      for (let i = 0; i < responses.length; i++) {
        if (!responses[i].ok) {
          const errorData = await responses[i].json();
          logger.error('Failed to update system configuration', {
            error: errorData,
            status: responses[i].status,
            property: configs[i].property
          });
          throw new Error(errorData.message || `Failed to update system configuration (${responses[i].status})`);
        }
      }
      
      // Parse all responses
      const dataPromises = responses.map(response => response.json());
      const allData = await Promise.all(dataPromises);
      
      logger.info('Successfully updated multiple system configurations', { count: configs.length });
      return allData.map(data => data.data);
    } catch (error) {
      logger.error('Error in updateMultipleConfigs', { error });
      throw error;
    }
  }
};